import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { listOf } from '../entity/entity';
import { tagTint } from '../plan/registries';
import type { Registries } from '../plan/registries';
import type { Arc, Status } from '../plan/arc';
import { cardFill, markInk } from '../design/tokens';
import { useTheme } from '../design/useTheme';
import { useCatalog } from '../entity/catalogStore';
import { usePlan } from '../plan/usePlan';
import { EMPTY_REGISTRIES } from '../plan/registries';
import { NookGlyph } from '../nook/NookGlyph';
import { tagTones } from '../design/tagPalette';
import type { Theme } from '../design/tokens';
import { StatusGlyph } from '../board/StatusGlyph';
import type { CatalogMap } from '../entity/catalogStore';
import { drawnShape, fitMiniature, miniatureShapes, stripHeight } from './miniature';
import type { Workspace } from './workspace';

/**
 * How an item on a plane is drawn in miniature, as the canvas draws it: an arc
 * in its tag's band with its status, anything else in the card fill of its
 * catalog row's first tag.
 */
export function useMiniLook(
  arcs: Arc[] | null,
  catalog: CatalogMap,
  registries: Registries,
  theme: Theme,
): (ref: string) => MiniLook {
  const byPath = useMemo(() => new Map((arcs ?? []).map((arc) => [arc.path, arc])), [arcs]);
  return (ref) => {
    const arc = byPath.get(ref);
    const tag = arc ? listOf(arc.frontMatter['tags'])[0] : catalog.get(ref)?.tags[0];
    const hue = tag ? tagTint(registries, tag) : null;
    if (arc) return { fill: hue ? tagTones(theme, hue).band : null, status: arc.status };
    return { fill: cardFill(theme, hue), status: null };
  };
}

const MINI_HEIGHT = 112;

export interface MiniLook {
  fill: string | null;
  status: Status | null;
}

/**
 * The nook's plane, drawn live from its `plane:` key: solid shapes where
 * things were placed, each in its own tag's colour and an arc with its
 * status, stickies in their tint or the nook's own colour, on the canvas's
 * dotted ground.
 */
export function Miniature({
  plane,
  kindAt,
  look,
  own,
  theme,
  height = MINI_HEIGHT,
  aspect,
  fitHeight,
  blank = false,
  flat,
}: {
  plane: Workspace['plane'];
  kindAt: (path: string) => string | null;
  look: (ref: string) => MiniLook;
  /** The nook's own band, for stickies left at the default tint. */
  own: string | null;
  theme: Theme;
  height?: number;
  /** Its height as a share of its width, in place of [height]: it grows with what holds it. */
  aspect?: number;
  /** Tall enough, within these bounds, to draw the layout as large as its width allows — in place of [height]. */
  fitHeight?: { min: number; max: number };
  /** Says "Blank" over the field when nothing is placed. */
  blank?: boolean;
  /** Drawn as a card's strip: the folder's ground, shapes in its chip and stickies in its edge. */
  flat?: { ground: string; chip: string; edge: string };
}) {
  const box = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState(360);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth || 360);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const shapes = miniatureShapes(plane, kindAt);
  const tall = fitHeight ? stripHeight(shapes, width, fitHeight.min, fitHeight.max) : aspect ? Math.round(width * aspect) : height;
  const fit = fitMiniature(shapes, { w: width, h: tall });

  return (
    <span
      ref={box}
      className="nooks-mini"
      style={{ height: tall, ...(flat ? { backgroundColor: flat.ground, backgroundImage: 'none' } : null) }}
      aria-hidden={blank ? undefined : 'true'}
    >
      {blank && shapes.length === 0 && <span className="nooks-mini-blank">Blank</span>}
      {fit &&
        shapes.map((shape, i) => {
          const item = shape.sticky ? null : look(shape.key.replace(/#.*$/, ''));
          const fill = flat
            ? shape.sticky
              ? flat.edge
              : flat.chip
            : shape.sticky
            ? shape.tagTint
              ? tagTones(theme, shape.tagTint).band
              : !shape.tintChosen && own
                ? own
                : `var(${shape.tint === 'green' ? '--stickyGreen' : '--stickyWarm'})`
            : (item?.fill ?? undefined);
          const at = drawnShape(shape, fit);
          return (
            <span
              key={`${i}:${shape.key}`}
              className={shape.sticky ? 'nooks-mini-sticky' : 'nooks-mini-card'}
              style={{
                ...at,
                borderRadius: Math.max(2, 18 * fit.scale),
                ...(fill ? { background: fill } : {}),
              }}
            >
              {!flat && item?.status && <StatusGlyph status={item.status} size={8} />}
            </span>
          );
        })}
    </span>
  );
}


/** The nook glyph in its first tag's colour, grey where it has none. */
export function TintedNookGlyph({ tags, size = 13 }: { tags?: readonly string[]; size?: number }) {
  const { theme } = useTheme();
  const { plan } = usePlan();
  const registries = plan.status === 'ready' ? plan.plan.registries : EMPTY_REGISTRIES;
  const hue = tags?.[0] ? tagTint(registries, tags[0]) : null;
  return <NookGlyph size={size} ink={hue ? markInk(theme, hue) : undefined} />;
}

/**
 * An unnamed nook where a row would carry its name: its canvas, small, inline
 * — or "Blank" in words where nothing is placed.
 */
export function NookThumb({ nook, width = 64, height = 26 }: { nook: Workspace; width?: number; height?: number }) {
  const { theme } = useTheme();
  const { plan } = usePlan();
  const catalog = useCatalog();
  const look = useMiniLook(
    plan.status === 'ready' ? plan.plan.arcs : null,
    catalog,
    plan.status === 'ready' ? plan.plan.registries : EMPTY_REGISTRIES,
    theme,
  );
  if (nook.plane.length === 0) return <span className="nook-blank-word">Blank</span>;
  return (
    <span className="nook-thumb" role="img" aria-label="Unnamed nook" style={{ width, height }}>
      <Miniature
        plane={nook.plane}
        kindAt={(path) => catalog.get(path)?.kind ?? null}
        look={look}
        own={null}
        theme={theme}
        height={height}
      />
    </span>
  );
}
